import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// Altura da barra de título (também usada pelo DesktopChrome para reservar espaço)
export const TITLEBAR_HEIGHT = 32;

// Ícones no estilo dos controles de janela do Windows/Discord (10x10)
function MinimizeIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
      <rect x="0" y="4.5" width="10" height="1" fill="currentColor" />
    </svg>
  );
}

function MaximizeIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
      <rect x="0.5" y="0.5" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
      <rect x="0.5" y="2.5" width="7" height="7" fill="none" stroke="currentColor" strokeWidth="1" />
      <path d="M2.5 2.5 V0.5 H9.5 V7.5 H7.5" fill="none" stroke="currentColor" strokeWidth="1" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
      <path d="M0.5 0.5 L9.5 9.5 M9.5 0.5 L0.5 9.5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

interface ControlButtonProps {
  onClick: () => void;
  label: string;
  danger?: boolean;
  children: React.ReactNode;
}

function ControlButton({ onClick, label, danger, children }: ControlButtonProps) {
  const theme = useTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label={label}
      sx={{
        // Não arrastável: precisa receber o clique
        WebkitAppRegion: 'no-drag',
        height: TITLEBAR_HEIGHT,
        width: 46,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        color: theme.palette.text.secondary,
        cursor: 'pointer',
        transition: 'background-color 120ms ease, color 120ms ease',
        '&:hover': {
          // Hover roxo (tom do tema); fechar continua vermelho
          backgroundColor: danger ? '#e81123' : alpha(theme.palette.primary.main, 0.18),
          color: danger ? '#fff' : theme.palette.primary.main
        }
      }}
    >
      {children}
    </Box>
  );
}

// ==============================|| BARRA DE TÍTULO CUSTOMIZADA (ELECTRON) ||============================== //

export default function TitleBar() {
  const theme = useTheme();
  const [maximized, setMaximized] = useState(false);
  const controls = window.desktop?.window;

  useEffect(() => {
    if (!controls) return;
    controls.isMaximized().then(setMaximized);
    const unsubscribe = controls.onMaximizedChange(setMaximized);
    return unsubscribe;
  }, [controls]);

  if (!controls) return null;

  return (
    <Box
      sx={{
        // A barra inteira é a região arrastável da janela
        WebkitAppRegion: 'drag',
        height: TITLEBAR_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // Tom roxo do tema: em dark vira violeta bem escuro, em light violeta bem claro.
        // Acompanha automaticamente o modo e o preset de cor escolhido no app.
        backgroundColor: theme.palette.primary.lighter,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.25 : 0.15)}`,
        userSelect: 'none'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5, overflow: 'hidden' }}>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.primary.main, fontWeight: 600, letterSpacing: 0.3, whiteSpace: 'nowrap' }}
        >
          Família
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <ControlButton label="Minimizar" onClick={() => controls.minimize()}>
          <MinimizeIcon />
        </ControlButton>
        <ControlButton label={maximized ? 'Restaurar' : 'Maximizar'} onClick={() => controls.toggleMaximize()}>
          {maximized ? <RestoreIcon /> : <MaximizeIcon />}
        </ControlButton>
        <ControlButton label="Fechar" danger onClick={() => controls.close()}>
          <CloseIcon />
        </ControlButton>
      </Box>
    </Box>
  );
}
